import * as actions from "./policyInsightsConflictsGetActions";
import { CURRENT_CHILD_ORGANIZATION_SET } from "store/domain/currentChildOrganization/currentChildOrganizationActions";

const initialState = {
  loaded: false,
  loading: false,
  loadingMore: false,
  error: null,
  data: null,
  // Conflict ids whose ack PATCH is in flight. A poll firing between
  // dismissOptimistic and the ack response would re-add the conflict because
  // the server hasn't persisted `isAck` yet — we filter the GET payload
  // against this set so dismissed rows don't flicker back in.
  pendingDismissedIds: [],
};

function mergeAppendedData(prev, next) {
  if (!prev) return next;
  return {
    ...next,
    conflicts: [...(prev.conflicts ?? []), ...(next.conflicts ?? [])],
  };
}

function filterPendingDismissed(data, pendingIds) {
  if (!data || !pendingIds || pendingIds.length === 0) return data;
  const pending = new Set(pendingIds);
  return {
    ...data,
    conflicts: (data.conflicts ?? []).filter((c) => !pending.has(c.id)),
  };
}

function decrementSummary(summary, severity) {
  if (!summary) return summary;
  const next = {
    ...summary,
    total: Math.max(0, (summary.total ?? 0) - 1),
  };
  if (severity && Object.prototype.hasOwnProperty.call(summary, severity)) {
    next[severity] = Math.max(0, (summary[severity] ?? 0) - 1);
  }
  return next;
}

function incrementSummary(summary, severity) {
  if (!summary) return summary;
  const next = {
    ...summary,
    total: (summary.total ?? 0) + 1,
  };
  if (severity && Object.prototype.hasOwnProperty.call(summary, severity)) {
    next[severity] = (summary[severity] ?? 0) + 1;
  }
  return next;
}

const ACTION_HANDLERS = {
  [actions.POLICY_INSIGHTS_CONFLICTS_GET_REQUEST]: (state, action) => {
    const append = action.append === true;
    return {
      ...state,
      loading: !append,
      loadingMore: append,
      loaded: append ? state.loaded : false,
      error: null,
      data: state.data,
    };
  },
  [actions.POLICY_INSIGHTS_CONFLICTS_GET_REQUEST_SUCCESS]: (state, action) => {
    const append = action.append === true;
    const filtered = filterPendingDismissed(
      action.data,
      state.pendingDismissedIds
    );
    return {
      ...state,
      loading: false,
      loadingMore: false,
      loaded: true,
      error: null,
      data: append ? mergeAppendedData(state.data, filtered) : filtered,
    };
  },
  [actions.POLICY_INSIGHTS_CONFLICTS_GET_REQUEST_FAILURE]: (state, action) => {
    const append = action.append === true;
    return {
      ...state,
      loading: false,
      loadingMore: false,
      // On append failure we keep the already-loaded pages so the user doesn't
      // lose context — only the first-page failure clears `data`.
      loaded: append ? state.loaded : false,
      error: action.error,
      data: append ? state.data : null,
    };
  },
  [actions.POLICY_INSIGHTS_CONFLICTS_DISMISS_OPTIMISTIC]: (state, action) => {
    if (!state.data) return state;
    const conflicts = state.data.conflicts ?? [];
    const target = conflicts.find((c) => c.id === action.id);
    if (!target) return state;
    const prevPending = state.pendingDismissedIds ?? [];
    return {
      ...state,
      pendingDismissedIds: prevPending.includes(action.id)
        ? prevPending
        : [...prevPending, action.id],
      data: {
        ...state.data,
        conflicts: conflicts.filter((c) => c.id !== action.id),
        summary: decrementSummary(state.data.summary, target.severity),
      },
    };
  },
  [actions.POLICY_INSIGHTS_CONFLICTS_DISMISS_REVERT]: (state, action) => {
    if (!state.data || !action.conflict) return state;
    const conflicts = state.data.conflicts ?? [];
    const prevPending = state.pendingDismissedIds ?? [];
    const nextPending = prevPending.filter((pid) => pid !== action.conflict.id);
    // Guard against double-revert if the same revert action fires twice.
    if (conflicts.some((c) => c.id === action.conflict.id)) {
      return prevPending === nextPending
        ? state
        : { ...state, pendingDismissedIds: nextPending };
    }
    const insertAt =
      typeof action.index === "number" &&
      action.index >= 0 &&
      action.index <= conflicts.length
        ? action.index
        : conflicts.length;
    const restored = [
      ...conflicts.slice(0, insertAt),
      action.conflict,
      ...conflicts.slice(insertAt),
    ];
    return {
      ...state,
      pendingDismissedIds: nextPending,
      data: {
        ...state.data,
        conflicts: restored,
        summary: incrementSummary(state.data.summary, action.conflict.severity),
      },
    };
  },
  [actions.POLICY_INSIGHTS_CONFLICTS_DISMISS_CONFIRMED]: (state, action) => {
    const prevPending = state.pendingDismissedIds ?? [];
    if (!prevPending.includes(action.id)) return state;
    return {
      ...state,
      pendingDismissedIds: prevPending.filter((pid) => pid !== action.id),
    };
  },
  [actions.POLICY_INSIGHTS_CONFLICTS_RESET]: () => initialState,
  [CURRENT_CHILD_ORGANIZATION_SET]: () => initialState,
};

export default function policyInsightsConflictsGetReducer(
  state = initialState,
  action
) {
  const handler = ACTION_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
